import { Checkbox, DatePicker, Form, FormInstance, Input, Spin, TimePicker } from 'antd';
import React, { useEffect, useState } from 'react'
import dayjs, { Dayjs } from 'dayjs';

import { CardBox } from './cardBox';
import { SearchItem } from 'pages/Components/SearchBox';
import CustomSelect from 'base/ui/components/CustomSelect';
import { services } from 'stores/root_store';
import { getRole, getRoles } from 'utils/localStorageHelper';
import { useTranslation } from 'react-i18next';

type GeneralInformationProps = {
  disabledField: () => boolean;
  wasteHandoverPlanId?: string;
  wasteHandoverRegistrationId?: string;
  role: string;
  accessUpdated: boolean;
  formCreate: FormInstance<any>;
  dataFromList?: any[],
  vendorNumber: string | undefined;
  canCheckEmailCheckbox?: () => boolean;
}

const GeneralInformation: React.FC<GeneralInformationProps> = (props) => {
  const {
    accessUpdated,
    disabledField,
    role,
    wasteHandoverPlanId,
    wasteHandoverRegistrationId,
    formCreate,
    dataFromList,
    vendorNumber,
    canCheckEmailCheckbox
  } = props
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [isCheckboxDisabled, setIsCheckboxDisabled] = useState(false);

  // Lấy roles từ localStorage và convert sang array nếu là string
  const rolesRaw = getRoles();
  const roles = Array.isArray(rolesRaw)
    ? rolesRaw
    : (typeof rolesRaw === 'string' ? rolesRaw.split(',').map(r => r.trim()) : []);

  useEffect(() => {
    // Luôn set isSendEmail về false, không theo giá trị từ API
    const role = getRole();
    if (wasteHandoverRegistrationId) {
      // Khi có wasteHandoverRegistrationId, luôn set về false
      formCreate.setFieldsValue({ isSendEmail: false });
    }
  }, [wasteHandoverPlanId, wasteHandoverRegistrationId, formCreate]);

  return (
    <CardBox title={t('waste-handover-plan-register.general-info.title')} >
      <div >
        {/* gửi mail */}
        <Spin spinning={loading}>
          <SearchItem initialValue={false} name={'isSendEmail'} valuePropName="checked" rules={[
            {
              validator: async (_, value) => {
                if (value) {
                  setLoading(true);
                  const data = await services.vendorServices.checkEmailVendor(vendorNumber || "");
                  setLoading(false);
                  if (!data?.data?.isHasMail) {
                    return Promise.reject(new Error(t('waste-handover-plan-register.general-info.email-missing')));
                  }
                }
              }
            }
          ]} >
            <Checkbox disabled={isCheckboxDisabled || (canCheckEmailCheckbox && !canCheckEmailCheckbox())}>{t('waste-handover-plan-register.general-info.auto-email')}</Checkbox>
          </SearchItem>
        </Spin>
        {/* Mã nhà cung cấp */}
        <SearchItem name='vendorNumber' hidden >
          <Input />
        </SearchItem>
        {/* nhà cung cấp */}
        <SearchItem name={'vendorName'} label={t('waste-handover-plan.vendor')}>
          <Input disabled />
        </SearchItem>
        {/* Tình trạng phiếu */}
        {wasteHandoverRegistrationId && (
          <SearchItem name={'taskStatusName'} label='Tình trạng phiếu'>
            <Input disabled />
          </SearchItem>
        )}
        {/* Thời gian bàn giao dự kiến */}
        <SearchItem required={true} label={t('waste-handover-plan.delivery-schedule-time')}>
          <Form.List name='deliveryTimeItems' rules={[{
            validator: async (_, value) => {
              if (!value || !value.length) {
                return Promise.reject(new Error(t('waste-handover-plan.delivery-schedule-time-required')));
              }
            },
          }]}>
            {(fiels, { add, remove }, { errors }) => <div className="flex flex-col gap-3">
              {fiels.map((fiel, index) => (
                <> <div key={index} className="w-full flex gap-default items-start">
                  {/* thời gian bàn giao */}
                  <SearchItem hidden name={[fiel.name, 'deliveryTime']}>
                    <Input />
                  </SearchItem>
                  <div className="flex w-full">
                    {/* hiện ngày bàn giao */}
                    <SearchItem name={[fiel.name, 'day']} noStyle rootClassName="!w-28">
                      <DatePicker
                        style={{ width: '100%' }}
                        format={'DD/MM/YYYY'}
                        disabled={disabledField() || !(role === "PP" && accessUpdated)}
                        allowClear={false}
                        onChange={(value) => {
                          const timeValue = formCreate.getFieldValue(['deliveryTimeItems', fiel.name, 'time']);
                          const timeStr = timeValue ? dayjs(timeValue).format("HH:mm") : "00:00";
                          formCreate.setFieldValue(['deliveryTimeItems', fiel.name, 'deliveryTime'],
                            `${value?.format("YYYY-MM-DD")}T${timeStr}:00`)
                        }}
                        className="!rounded-r-none" />
                    </SearchItem>
                    {/* giờ bàn giao */}
                    <SearchItem
                      name={[fiel.name, 'time']}
                      rootClassName="flex-1"
                    >
                      <TimePicker
                        // Chỉ role BGA_CT được phép chỉnh sửa giờ bàn giao (ngoài disabledField)
                        disabled={disabledField() || !roles.includes('BGA_CT')}
                        onChange={(value) => {
                          const dateValue = !wasteHandoverPlanId ?
                            dayjs(dataFromList?.at(0)?.deliveryDate).format("YYYY-MM-DD") :
                            dayjs(formCreate.getFieldValue(['deliveryTimeItems', fiel.name, 'deliveryTime'])).format('YYYY-MM-DD');
                          const timeStr = value ? value.format("HH:mm") : "00:00";
                          formCreate.setFieldValue(['deliveryTimeItems', fiel.name, 'deliveryTime'],
                            `${dateValue}T${timeStr}:00`)
                        }}
                        changeOnBlur
                        className="w-full !rounded-l-none"
                        format={"HH:mm"}
                        placeholder="HH:mm"
                      />
                    </SearchItem >
                  </div>
                </div>
                  <SearchItem rules={[{ required: true, message: t('waste-handover-plan.delivery-schedule-time-required') }]}>
                    <span className='italic text-red-400 flex-wrap'>{t('waste-handover-plan-register.general-info.working-hours-morning')}</span>
                    <div className='italic text-red-400 block items-end ml-[75px]'>{t('waste-handover-plan-register.general-info.working-hours-afternoon')}</div>
                  </SearchItem>
                </>

              ))}
              <Form.ErrorList errors={errors} />
            </div>}
          </Form.List>
        </SearchItem>
      </div>
    </CardBox>
  )
}

export default GeneralInformation

